import {
  Box,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { CasLoader } from "../brand/CasLoader";
import React, { useState } from "react";
import {
  useFetchInstalledListener,
  useFetchPackagesListener,
} from "../../hook/marketplace";
import { NoteBlockContainer, NotePageContainer } from "../note";
import { usePackages, useInstalledPackages } from "../../hook/package";
import { useAppIdMap } from "../../hook/app";
import { installPackage, uninstallPackage } from "../../api/package";

export const InstallComponentButton: React.FC<{
  packageId: string;
  appId: string;
  installed: boolean;
  onToggle: () => void;
}> = ({ packageId, appId, installed, onToggle }) => {
  const [loading, setLoading] = useState(false);

  const handleChange = async () => {
    setLoading(true);
    try {
      if (installed) {
        await uninstallPackage({ "app-id": appId, "package-id": packageId });
      } else {
        await installPackage({ "app-id": appId, "package-id": packageId });
      }
      onToggle();
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CasLoader size={20} />;
  return <Switch checked={installed} onChange={handleChange} />;
};

export const MarketplacePage: React.FC = () => {
  useFetchPackagesListener();
  const { appId } = useAppIdMap();
  useFetchInstalledListener(appId);

  const packagesMap = usePackages();
  const packages = Object.values(packagesMap);
  const installedPackages = useInstalledPackages();

  // Track local toggle state for installs (re-fetch would be cleaner but this keeps it snappy)
  const [localInstalled, setLocalInstalled] = useState<Record<string, boolean>>(
    {},
  );

  const isInstalled = (packageId: string) => {
    if (packageId in localInstalled) return localInstalled[packageId];
    return installedPackages.some((i) => i["package-id"] === packageId);
  };

  return (
    <NotePageContainer pageName="Marketplace">
      <NoteBlockContainer name="Marketplace">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Publisher</TableCell>
                <TableCell>Package</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Installed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => {
                return (
                  <TableRow key={pkg["package-id"]}>
                    <TableCell>{pkg["app-id"]}</TableCell>
                    <TableCell>{pkg.name}</TableCell>
                    <TableCell>{pkg.description ?? ""}</TableCell>
                    <TableCell>
                      {appId && (
                        <InstallComponentButton
                          packageId={pkg["package-id"]}
                          appId={appId}
                          installed={isInstalled(pkg["package-id"])}
                          onToggle={() =>
                            setLocalInstalled((prev) => ({
                              ...prev,
                              [pkg["package-id"]]: !isInstalled(
                                pkg["package-id"],
                              ),
                            }))
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </NoteBlockContainer>
    </NotePageContainer>
  );
};
